import clsx from 'clsx';
import React, { forwardRef, type HTMLAttributes } from 'react';

import styles from './Spinner.module.css';

export interface SpinnerProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
    /**
     * Ring diameter in px. Without it the ring is `1em` of the surrounding text, so it scales with the type it sits
     * beside. A parent can instead set `--spinner-size` in CSS (`Button` does, from its glyph size).
     */
    size?: number;
}

/**
 * The loading ring: a border-only circle with a transparent quarter, spun in `currentColor`, so it takes the text
 * colour of wherever it sits. It is decorative (`aria-hidden`) — the words beside it, or an `aria-busy` parent such as
 * a loading `Button`, carry the meaning. For a ring that stands alone, with a value, a status or an accessible name,
 * use `CircularProgress`.
 *
 * Reduced motion slows the ring rather than freezing it; a still ring reads as "stuck".
 *
 * @example
 * <Flex as="span" inline align="center" gap={2}>
 *     <Spinner size={16} />
 *     Searching…
 * </Flex>
 */
const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(({ size, className, style, ...rest }, ref) => (
    <span
        ref={ref}
        aria-hidden="true"
        className={clsx(styles.spinner, className)}
        style={size === undefined ? style : ({ '--spinner-size': `${size}px`, ...style } as React.CSSProperties)}
        {...rest}
    />
));

Spinner.displayName = 'Spinner';

export { Spinner };
export default Spinner;
